/**
 * Challenge Detail Screen
 * Admin view for managing a challenge and reviewing submissions
 */

import { useState, useMemo, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  RefreshControl,
  TextInput,
  Modal,
} from "react-native";
import { Image } from "expo-image";
import { SafeAreaView } from "react-native-safe-area-context";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { format, formatDistanceToNow, isPast, isFuture } from "date-fns";
import { nl } from "date-fns/locale";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  useChallengeDetail,
  CompletionWithProfile,
} from "@/modules/challenges/hooks/useChallengeDetail";
import {
  CHALLENGE_ICONS,
  CHALLENGE_LABELS,
  CHALLENGE_COLORS,
  ChallengeStatus,
  CompletionStatus,
  PhotoChallengeConfig,
} from "@/modules/challenges/types";
import { CreatePhotoChallengeModal, CreateOpdrachtModal } from "@/modules/challenges";

/**
 * Status badge colors
 */
const STATUS_COLORS: Record<ChallengeStatus, { bg: string; text: string }> = {
  draft: { bg: "#6B7280", text: "#FFFFFF" },
  active: { bg: "#10B981", text: "#FFFFFF" },
  completed: { bg: "#3B82F6", text: "#FFFFFF" },
  archived: { bg: "#9CA3AF", text: "#FFFFFF" },
};

const COMPLETION_STATUS_COLORS: Record<CompletionStatus, { bg: string; text: string }> = {
  pending: { bg: "#F59E0B", text: "#FFFFFF" },
  approved: { bg: "#10B981", text: "#FFFFFF" },
  auto_approved: { bg: "#10B981", text: "#FFFFFF" },
  rejected: { bg: "#EF4444", text: "#FFFFFF" },
};

const STATUS_LABELS: Record<ChallengeStatus, string> = {
  draft: "Concept",
  active: "Actief",
  completed: "Voltooid",
  archived: "Gearchiveerd",
};

const COMPLETION_STATUS_LABELS: Record<CompletionStatus, string> = {
  pending: "In afwachting",
  approved: "Goedgekeurd",
  auto_approved: "Auto-goedgekeurd",
  rejected: "Afgekeurd",
};

/**
 * Challenge Detail Screen
 */
export default function ChallengeDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { profile, roles } = useAuth();

  const {
    challenge,
    submissions,
    isLoading,
    error,
    refetch,
    updateStatus,
    deleteChallenge,
    reviewSubmission,
    stats,
  } = useChallengeDetail(id || "");

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<"info" | "submissions">("info");
  const [selectedSubmission, setSelectedSubmission] = useState<CompletionWithProfile | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvePoints, setApprovePoints] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [teamName, setTeamName] = useState<string | null>(null);

  const isFamilieAdmin = roles.isFamilieAdmin || roles.isSuperAdmin;

  /**
   * Fetch team name if challenge is for a specific team
   */
  useEffect(() => {
    const fetchTeamName = async () => {
      if (challenge?.visibility === "specific_team" && challenge.visible_to_team_id) {
        try {
          const { data, error } = await supabase
            .from("teams")
            .select("name")
            .eq("id", challenge.visible_to_team_id)
            .single();

          if (!error && data) {
            setTeamName(data.name);
          }
        } catch (err) {
          console.error("Error fetching team:", err);
        }
      } else {
        setTeamName(null);
      }
    };

    fetchTeamName();
  }, [challenge?.visibility, challenge?.visible_to_team_id]);

  /**
   * Handle refresh
   */
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  /**
   * Handle status change
   */
  const handleStatusChange = (newStatus: ChallengeStatus) => {
    const statusLabel = STATUS_LABELS[newStatus];

    Alert.alert(
      `Status wijzigen`,
      `Weet je zeker dat je de status wilt wijzigen naar "${statusLabel}"?`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Wijzigen",
          onPress: async () => {
            const success = await updateStatus(newStatus);
            if (success) {
              Alert.alert("Succes", `Status gewijzigd naar ${statusLabel}`);
            }
          },
        },
      ]
    );
  };

  /**
   * Handle delete
   */
  const handleDelete = () => {
    Alert.alert(
      "Uitdaging verwijderen",
      "Weet je zeker dat je deze uitdaging wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            const success = await deleteChallenge();
            if (success) {
              router.back();
            }
          },
        },
      ]
    );
  };

  /**
   * Handle approve submission
   */
  /**
   * Open approve modal
   */
  const openApproveModal = (submission: CompletionWithProfile) => {
    setSelectedSubmission(submission);
    setApprovePoints(challenge?.points?.toString() || "0");
    setShowApproveModal(true);
  };

  /**
   * Handle approve submission
   */
  const handleApprove = async () => {
    if (!selectedSubmission) return;

    const points = parseInt(approvePoints, 10) || 0;
    const success = await reviewSubmission(selectedSubmission.id, "approved", undefined, points);
    
    if (success) {
      setShowApproveModal(false);
      setSelectedSubmission(null);
      Alert.alert("Succes", `Inzending goedgekeurd met ${points} punten!`);
    }
  };

  /**
   * Handle reject submission
   */
  const handleReject = async () => {
    if (!selectedSubmission) return;

    const success = await reviewSubmission(
      selectedSubmission.id,
      "rejected",
      rejectionReason || "Geen reden opgegeven"
    );

    if (success) {
      setShowRejectModal(false);
      setSelectedSubmission(null);
      setRejectionReason("");
      Alert.alert("Succes", "Inzending afgekeurd");
    }
  };

  /**
   * Open reject modal
   */
  const openRejectModal = (submission: CompletionWithProfile) => {
    setSelectedSubmission(submission);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  /**
   * Handle set back to pending
   */
  const handleSetPending = async (submission: CompletionWithProfile) => {
    Alert.alert(
      "Terug naar in afwachting?",
      "Dit zet de inzending terug naar 'in afwachting' zodat het team deze kan wijzigen.",
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Ja, terug naar in afwachting",
          onPress: async () => {
            const success = await reviewSubmission(submission.id, "pending");
            if (success) {
              Alert.alert("Succes", "Inzending teruggezet naar in afwachting");
            }
          },
        },
      ]
    );
  };

  /**
   * Get challenge availability status
   */
  const availabilityStatus = useMemo(() => {
    if (!challenge) return null;

    const now = new Date();
    const from = challenge.available_from ? new Date(challenge.available_from) : null;
    const until = challenge.available_until ? new Date(challenge.available_until) : null;

    if (from && isFuture(from)) {
      return {
        label: "Start over",
        value: formatDistanceToNow(from, { locale: nl }),
        color: colors.warning,
      };
    }

    if (until && isPast(until)) {
      return {
        label: "Verlopen",
        value: format(until, "d MMM HH:mm", { locale: nl }),
        color: colors.error,
      };
    }

    if (from && until) {
      return {
        label: "Beschikbaar tot",
        value: format(until, "d MMM HH:mm", { locale: nl }),
        color: colors.success,
      };
    }

    return {
      label: "Altijd beschikbaar",
      value: "",
      color: colors.success,
    };
  }, [challenge, colors]);

  // Loading state
  if (isLoading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }}>
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={{ color: colors.textSecondary }} className="mt-4">
            Laden...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Error state
  if (error || !challenge) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }}>
        <View className="flex-1 items-center justify-center p-6">
          <Ionicons name="warning" size={48} color={colors.error} />
          <Text
            style={{ color: colors.text }}
            className="text-lg font-semibold mt-4 text-center"
          >
            {error || "Uitdaging niet gevonden"}
          </Text>
          <TouchableOpacity
            onPress={() => router.back()}
            className="mt-6 px-6 py-3 rounded-xl"
            style={{ backgroundColor: colors.primary }}
          >
            <Text className="text-white font-semibold">Terug</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const challengeColor = CHALLENGE_COLORS[challenge.challenge_type] || colors.primary;
  const config = challenge.config as PhotoChallengeConfig;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={["top"]}>
      {/* Header */}
      <View
        className="flex-row items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <TouchableOpacity
          onPress={() => router.back()}
          className="p-2 -ml-2"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>

        <View className="flex-1 mx-4">
          <Text
            style={{ color: colors.text }}
            className="text-lg font-bold text-center"
            numberOfLines={1}
          >
            {challenge.title}
          </Text>
        </View>

        <View className="flex-row items-center">
          {/* Edit button - for photo and opdracht challenges */}
          {(challenge.challenge_type === "photo" || challenge.challenge_type === "opdracht") && (
            <TouchableOpacity
              onPress={() => setShowEditModal(true)}
              className="p-2 mr-2"
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="create-outline" size={22} color={colors.primary} />
            </TouchableOpacity>
          )}

          <TouchableOpacity
            onPress={handleDelete}
            className="p-2 -mr-2"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="trash-outline" size={22} color={colors.error} />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        className="flex-1"
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Status Banner */}
        <View
          className="mx-4 mt-4 p-4 rounded-2xl flex-row items-center justify-between"
          style={{ backgroundColor: challengeColor + "20" }}
        >
          <View className="flex-row items-center flex-1">
            <View
              className="w-12 h-12 rounded-full items-center justify-center"
              style={{ backgroundColor: challengeColor }}
            >
              <Ionicons
                name={CHALLENGE_ICONS[challenge.challenge_type] as any}
                size={24}
                color="#FFFFFF"
              />
            </View>
            <View className="ml-3 flex-1">
              <Text style={{ color: colors.textSecondary }} className="text-sm">
                {CHALLENGE_LABELS[challenge.challenge_type]}
              </Text>
              <View
                className="self-start px-2 py-0.5 rounded-full mt-1"
                style={{
                  backgroundColor: STATUS_COLORS[challenge.status].bg,
                }}
              >
                <Text className="text-xs font-medium" style={{ color: "#FFFFFF" }}>
                  {STATUS_LABELS[challenge.status]}
                </Text>
              </View>
            </View>
          </View>

          {challenge.has_points && (
            <View className="items-center">
              <Text style={{ color: challengeColor }} className="text-2xl font-bold">
                {challenge.points}
              </Text>
              <Text style={{ color: colors.textSecondary }} className="text-xs">
                punten
              </Text>
            </View>
          )}
        </View>

        {/* Quick Actions */}
        <View className="mx-4 mt-4">
          <Text style={{ color: colors.text }} className="text-sm font-semibold mb-3">
            Status wijzigen
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View className="flex-row gap-2">
              {challenge.status === "draft" && (
                <TouchableOpacity
                  onPress={() => handleStatusChange("active")}
                  className="px-4 py-2 rounded-full flex-row items-center"
                  style={{ backgroundColor: STATUS_COLORS.active.bg }}
                >
                  <Ionicons name="play" size={16} color="#FFFFFF" />
                  <Text className="text-white font-medium ml-1">Activeren</Text>
                </TouchableOpacity>
              )}

              {challenge.status === "active" && (
                <>
                  <TouchableOpacity
                    onPress={() => handleStatusChange("draft")}
                    className="px-4 py-2 rounded-full flex-row items-center"
                    style={{ backgroundColor: STATUS_COLORS.draft.bg }}
                  >
                    <Ionicons name="pause" size={16} color="#FFFFFF" />
                    <Text className="text-white font-medium ml-1">Pauzeren</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => handleStatusChange("completed")}
                    className="px-4 py-2 rounded-full flex-row items-center"
                    style={{ backgroundColor: STATUS_COLORS.completed.bg }}
                  >
                    <Ionicons name="checkmark-circle" size={16} color="#FFFFFF" />
                    <Text className="text-white font-medium ml-1">Afsluiten</Text>
                  </TouchableOpacity>
                </>
              )}

              {(challenge.status === "completed" || challenge.status === "archived") && (
                <TouchableOpacity
                  onPress={() => handleStatusChange("active")}
                  className="px-4 py-2 rounded-full flex-row items-center"
                  style={{ backgroundColor: STATUS_COLORS.active.bg }}
                >
                  <Ionicons name="refresh" size={16} color="#FFFFFF" />
                  <Text className="text-white font-medium ml-1">Heractiveren</Text>
                </TouchableOpacity>
              )}

              {challenge.status !== "archived" && (
                <TouchableOpacity
                  onPress={() => handleStatusChange("archived")}
                  className="px-4 py-2 rounded-full flex-row items-center"
                  style={{ backgroundColor: STATUS_COLORS.archived.bg }}
                >
                  <Ionicons name="archive" size={16} color="#FFFFFF" />
                  <Text className="text-white font-medium ml-1">Archiveren</Text>
                </TouchableOpacity>
              )}
            </View>
          </ScrollView>
        </View>

        {/* Stats Cards */}
        <View className="flex-row mx-4 mt-4 gap-3">
          <View
            className="flex-1 p-3 rounded-xl"
            style={{ backgroundColor: isDark ? colors.surface : "#F3F4F6" }}
          >
            <Text style={{ color: colors.textSecondary }} className="text-xs">
              Totaal inzendingen
            </Text>
            <Text style={{ color: colors.text }} className="text-2xl font-bold">
              {stats.total}
            </Text>
          </View>

          <View
            className="flex-1 p-3 rounded-xl"
            style={{ backgroundColor: "#F59E0B20" }}
          >
            <Text style={{ color: "#F59E0B" }} className="text-xs">
              In afwachting
            </Text>
            <Text style={{ color: "#F59E0B" }} className="text-2xl font-bold">
              {stats.pending}
            </Text>
          </View>

          <View
            className="flex-1 p-3 rounded-xl"
            style={{ backgroundColor: "#10B98120" }}
          >
            <Text style={{ color: "#10B981" }} className="text-xs">
              Goedgekeurd
            </Text>
            <Text style={{ color: "#10B981" }} className="text-2xl font-bold">
              {stats.approved}
            </Text>
          </View>
        </View>

        {/* Tabs */}
        <View
          className="flex-row mx-4 mt-6 p-1 rounded-xl"
          style={{ backgroundColor: isDark ? colors.surface : "#F3F4F6" }}
        >
          <TouchableOpacity
            onPress={() => setActiveTab("info")}
            className="flex-1 py-2 rounded-lg"
            style={{
              backgroundColor: activeTab === "info" ? colors.surface : "transparent",
            }}
          >
            <Text
              style={{
                color: activeTab === "info" ? colors.primary : colors.textSecondary,
              }}
              className="text-center font-medium"
            >
              Details
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setActiveTab("submissions")}
            className="flex-1 py-2 rounded-lg flex-row items-center justify-center"
            style={{
              backgroundColor: activeTab === "submissions" ? colors.surface : "transparent",
            }}
          >
            <Text
              style={{
                color: activeTab === "submissions" ? colors.primary : colors.textSecondary,
              }}
              className="text-center font-medium"
            >
              Inzendingen
            </Text>
            {stats.pending > 0 && (
              <View
                className="ml-2 w-5 h-5 rounded-full items-center justify-center"
                style={{ backgroundColor: "#F59E0B" }}
              >
                <Text className="text-white text-xs font-bold">{stats.pending}</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>

        {/* Tab Content */}
        {activeTab === "info" ? (
          <View className="mx-4 mt-4 mb-8">
            {/* Description */}
            {challenge.description && (
              <View
                className="p-4 rounded-xl mb-4"
                style={{ backgroundColor: colors.surface }}
              >
                <Text
                  style={{ color: colors.textSecondary }}
                  className="text-sm font-medium mb-2"
                >
                  Beschrijving
                </Text>
                <Text style={{ color: colors.text }}>{challenge.description}</Text>
              </View>
            )}

            {/* Info Grid */}
            <View
              className="p-4 rounded-xl"
              style={{ backgroundColor: colors.surface }}
            >
              <Text
                style={{ color: colors.textSecondary }}
                className="text-sm font-medium mb-3"
              >
                Instellingen
              </Text>

              <View className="space-y-3">
                {/* Target Type */}
                <View className="flex-row items-center justify-between py-2 border-b" style={{ borderColor: colors.border }}>
                  <View className="flex-row items-center">
                    <Ionicons name="people" size={18} color={colors.textSecondary} />
                    <Text style={{ color: colors.textSecondary }} className="ml-2">
                      Type
                    </Text>
                  </View>
                  <Text style={{ color: colors.text }} className="font-medium">
                    {challenge.target_type === "team" ? "Team" : "Individueel"}
                  </Text>
                </View>

                {/* Visibility */}
                <View className="flex-row items-center justify-between py-2 border-b" style={{ borderColor: colors.border }}>
                  <View className="flex-row items-center">
                    <Ionicons name="eye" size={18} color={colors.textSecondary} />
                    <Text style={{ color: colors.textSecondary }} className="ml-2">
                      Zichtbaarheid
                    </Text>
                  </View>
                  <View className="items-end flex-shrink">
                    <Text style={{ color: colors.text }} className="font-medium">
                      {challenge.visibility === "all"
                        ? "Iedereen"
                        : challenge.visibility === "specific_team"
                        ? "Specifiek team"
                        : "Specifieke gebruikers"}
                    </Text>
                    {challenge.visibility === "specific_team" && teamName && (
                      <Text style={{ color: colors.primary }} className="text-sm mt-0.5">
                        {teamName}
                      </Text>
                    )}
                  </View>
                </View>

                {/* Time Limit */}
                {challenge.time_limit_minutes > 0 && (
                  <View className="flex-row items-center justify-between py-2 border-b" style={{ borderColor: colors.border }}>
                    <View className="flex-row items-center">
                      <Ionicons name="timer" size={18} color={colors.textSecondary} />
                      <Text style={{ color: colors.textSecondary }} className="ml-2">
                        Tijdslimiet
                      </Text>
                    </View>
                    <Text style={{ color: colors.text }} className="font-medium">
                      {challenge.time_limit_minutes} min
                    </Text>
                  </View>
                )}

                {/* Availability */}
                {availabilityStatus && (
                  <View className="flex-row items-center justify-between py-2 border-b" style={{ borderColor: colors.border }}>
                    <View className="flex-row items-center">
                      <Ionicons name="calendar" size={18} color={colors.textSecondary} />
                      <Text style={{ color: colors.textSecondary }} className="ml-2">
                        {availabilityStatus.label}
                      </Text>
                    </View>
                    <Text style={{ color: availabilityStatus.color }} className="font-medium">
                      {availabilityStatus.value}
                    </Text>
                  </View>
                )}

                {/* Photo Challenge Specific */}
                {challenge.challenge_type === "photo" && config && (
                  <>
                    <View className="flex-row items-center justify-between py-2 border-b" style={{ borderColor: colors.border }}>
                      <View className="flex-row items-center">
                        <Ionicons name="checkmark-circle" size={18} color={colors.textSecondary} />
                        <Text style={{ color: colors.textSecondary }} className="ml-2">
                          Verificatie
                        </Text>
                      </View>
                      <Text style={{ color: colors.text }} className="font-medium">
                        {config.verification_type === "admin" ? "Door admin" : "Automatisch"}
                      </Text>
                    </View>

                    {config.require_location && (
                      <View className="flex-row items-center justify-between py-2 border-b" style={{ borderColor: colors.border }}>
                        <View className="flex-row items-center">
                          <Ionicons name="location" size={18} color={colors.textSecondary} />
                          <Text style={{ color: colors.textSecondary }} className="ml-2">
                            Locatie vereist
                          </Text>
                        </View>
                        <Ionicons name="checkmark" size={18} color={colors.success} />
                      </View>
                    )}

                    {config.min_people > 1 && (
                      <View className="flex-row items-center justify-between py-2" style={{ borderColor: colors.border }}>
                        <View className="flex-row items-center">
                          <Ionicons name="people" size={18} color={colors.textSecondary} />
                          <Text style={{ color: colors.textSecondary }} className="ml-2">
                            Min. personen
                          </Text>
                        </View>
                        <Text style={{ color: colors.text }} className="font-medium">
                          {config.min_people}
                        </Text>
                      </View>
                    )}
                  </>
                )}
              </View>
            </View>

            {/* Created Info */}
            <View className="mt-4 items-center">
              <Text style={{ color: colors.textSecondary }} className="text-sm">
                Aangemaakt op{" "}
                {format(new Date(challenge.created_at), "d MMMM yyyy 'om' HH:mm", {
                  locale: nl,
                })}
              </Text>
            </View>
          </View>
        ) : (
          /* Submissions Tab */
          <View className="mx-4 mt-4 mb-8">
            {submissions.length === 0 ? (
              <View
                className="p-8 rounded-xl items-center"
                style={{ backgroundColor: colors.surface }}
              >
                <Ionicons name="document-text-outline" size={48} color={colors.textSecondary} />
                <Text
                  style={{ color: colors.textSecondary }}
                  className="mt-4 text-center"
                >
                  Nog geen inzendingen ontvangen
                </Text>
              </View>
            ) : (
              <View>
                {submissions.map((submission) => (
                  <SubmissionCard
                    key={submission.id}
                    submission={submission}
                    colors={colors}
                    isDark={isDark}
                    onApprove={() => openApproveModal(submission)}
                    onReject={() => openRejectModal(submission)}
                    onSetPending={() => handleSetPending(submission)}
                    challengeType={challenge.challenge_type}
                  />
                ))}
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {/* Reject Modal */}
      <Modal
        visible={showRejectModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowRejectModal(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-6">
          <View
            className="w-full rounded-2xl p-6"
            style={{ backgroundColor: colors.surface }}
          >
            <Text style={{ color: colors.text }} className="text-lg font-bold mb-4">
              Inzending afkeuren
            </Text>

            <Text style={{ color: colors.textSecondary }} className="mb-2">
              Reden voor afkeuring (optioneel)
            </Text>

            <TextInput
              value={rejectionReason}
              onChangeText={setRejectionReason}
              placeholder="Bijv. Foto is niet duidelijk..."
              placeholderTextColor={colors.textSecondary}
              multiline
              numberOfLines={3}
              className="p-3 rounded-xl mb-4"
              style={{
                backgroundColor: isDark ? colors.surface : "#F3F4F6",
                color: colors.text,
                textAlignVertical: "top",
              }}
            />

            <View className="flex-row gap-3">
              <TouchableOpacity
                onPress={() => setShowRejectModal(false)}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: isDark ? colors.surface : "#F3F4F6" }}
              >
                <Text style={{ color: colors.text }} className="font-semibold">
                  Annuleren
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleReject}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.error }}
              >
                <Text className="text-white font-semibold">Afkeuren</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Approve Modal */}
      <Modal
        visible={showApproveModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowApproveModal(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-6">
          <View
            className="w-full rounded-2xl p-6"
            style={{ backgroundColor: colors.surface }}
          >
            <Text style={{ color: colors.text }} className="text-lg font-bold mb-4">
              Inzending goedkeuren
            </Text>

            {/* Submission preview */}
            {selectedSubmission && (
              <View className="mb-4">
                {/* Photo preview if available */}
                {challenge?.challenge_type === "photo" && (selectedSubmission.submission as any)?.photo_url && (
                  <Image
                    source={{ uri: (selectedSubmission.submission as any).photo_url }}
                    style={{ width: "100%", height: 160, borderRadius: 12, marginBottom: 12 }}
                    contentFit="cover"
                  />
                )}
                <View className="flex-row items-center">
                  <View
                    className="w-8 h-8 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.primary + "20" }}
                  >
                    <Ionicons name="person" size={16} color={colors.primary} />
                  </View>
                  <Text style={{ color: colors.text }} className="ml-2 font-medium">
                    {selectedSubmission.profile?.nickname || selectedSubmission.profile?.full_name || "Onbekend"}
                  </Text>
                </View>
              </View>
            )}

            <Text style={{ color: colors.textSecondary }} className="mb-2">
              Punten toekennen
            </Text>

            <View className="flex-row items-center mb-4">
              <TouchableOpacity
                onPress={() => setApprovePoints(Math.max(0, parseInt(approvePoints, 10) - 1).toString())}
                className="w-12 h-12 rounded-xl items-center justify-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="remove" size={24} color={colors.text} />
              </TouchableOpacity>

              <TextInput
                value={approvePoints}
                onChangeText={setApprovePoints}
                keyboardType="number-pad"
                className="flex-1 mx-3 py-3 rounded-xl text-center text-2xl font-bold"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.primary,
                }}
              />

              <TouchableOpacity
                onPress={() => setApprovePoints((parseInt(approvePoints, 10) + 1).toString())}
                className="w-12 h-12 rounded-xl items-center justify-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="add" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>

            {/* Quick point buttons */}
            <View className="flex-row gap-2 mb-4">
              {[0, challenge?.points ? Math.floor(challenge.points / 2) : 5, challenge?.points || 10, challenge?.points ? challenge.points + (challenge.bonus_points || 5) : 15].map((pts) => (
                <TouchableOpacity
                  key={pts}
                  onPress={() => setApprovePoints(pts.toString())}
                  className="flex-1 py-2 rounded-lg items-center"
                  style={{
                    backgroundColor: approvePoints === pts.toString() ? colors.primary : colors.surfaceSecondary,
                  }}
                >
                  <Text
                    style={{ color: approvePoints === pts.toString() ? "#FFFFFF" : colors.text }}
                    className="font-medium"
                  >
                    {pts}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <View className="flex-row gap-3">
              <TouchableOpacity
                onPress={() => setShowApproveModal(false)}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: isDark ? colors.surfaceSecondary : "#F3F4F6" }}
              >
                <Text style={{ color: colors.text }} className="font-semibold">
                  Annuleren
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleApprove}
                className="flex-1 py-3 rounded-xl items-center flex-row justify-center"
                style={{ backgroundColor: colors.success }}
              >
                <Ionicons name="checkmark" size={20} color="#FFFFFF" />
                <Text className="text-white font-semibold ml-1">Goedkeuren</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Modal - for photo challenges */}
      {challenge.challenge_type === "photo" && (
        <CreatePhotoChallengeModal
          visible={showEditModal}
          onClose={() => setShowEditModal(false)}
          editChallenge={challenge}
          onUpdated={() => {
            refetch();
          }}
        />
      )}

      {/* Edit Modal - for opdracht challenges */}
      {challenge.challenge_type === "opdracht" && (
        <CreateOpdrachtModal
          visible={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            refetch();
          }}
          weekendId={challenge.weekend_id || undefined}
          editChallenge={{
            id: challenge.id,
            title: challenge.title,
            description: challenge.description,
            target_type: challenge.target_type,
            visibility: challenge.visibility,
            visible_to_team_id: challenge.visible_to_team_id,
            config: challenge.config,
            linked_event_id: challenge.linked_event_id,
            reveal_targets: challenge.reveal_targets,
          }}
        />
      )}
    </SafeAreaView>
  );
}

/**
 * Submission Card Component
 */
interface SubmissionCardProps {
  submission: CompletionWithProfile;
  colors: ReturnType<typeof useThemeColors>;
  isDark: boolean;
  onApprove: () => void;
  onReject: () => void;
  onSetPending: () => void;
  challengeType: string;
}

function SubmissionCard({
  submission,
  colors,
  isDark,
  onApprove,
  onReject,
  onSetPending,
  challengeType,
}: SubmissionCardProps) {
  const statusColor = COMPLETION_STATUS_COLORS[submission.status];
  const isPending = submission.status === "pending";
  const isApproved = submission.status === "approved" || submission.status === "auto_approved";

  // Get submitter name
  const submitterName = submission.profile
    ? submission.profile.nickname || submission.profile.full_name || "Onbekend"
    : "Onbekend";

  // Get team name if applicable
  const teamName = submission.team?.name;

  // Check if this is a section completion
  const isSectionCompletion = (submission as any)?.is_section_completion === true;
  const sectionTitle = (submission as any)?.section_title;

  // Photo URL for photo challenges or photo sections
  const photoUrl =
    challengeType === "photo"
      ? (submission.submission as any)?.photo_url
      : isSectionCompletion && (submission.submission as any)?.photo_url
      ? (submission.submission as any)?.photo_url
      : null;

  // Text answer for text sections
  const textAnswer = isSectionCompletion ? (submission.submission as any)?.answer : null;

  // Number value for number sections
  const numberValue = isSectionCompletion ? (submission.submission as any)?.value : null;

  // Checkbox completion
  const isCheckboxComplete = isSectionCompletion && (submission.submission as any)?.completed === true;

  return (
    <View
      style={{ 
        backgroundColor: colors.surface,
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 16,
      }}
    >
      {/* Photo Preview */}
      {photoUrl ? (
        <Image
          source={{ uri: photoUrl }}
          style={{ 
            width: "100%", 
            height: 200,
            backgroundColor: colors.surfaceSecondary,
          }}
          contentFit="cover"
          cachePolicy="disk"
          onError={(e) => console.log("Image load error:", e.error)}
          onLoad={() => console.log("Image loaded successfully")}
        />
      ) : challengeType === "photo" ? (
        <View 
          style={{ 
            width: "100%", 
            height: 120, 
            backgroundColor: colors.surfaceSecondary,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Ionicons name="image-outline" size={48} color={colors.textTertiary} />
          <Text style={{ color: colors.textTertiary, marginTop: 8, fontSize: 14 }}>
            Geen foto gevonden
          </Text>
        </View>
      ) : null}

      <View className="p-4">
        {/* Header */}
        <View className="flex-row items-center justify-between mb-3">
          <View className="flex-row items-center flex-1">
            {submission.profile?.avatar_url ? (
              <Image
                source={{ uri: submission.profile.avatar_url }}
                style={{ width: 40, height: 40, borderRadius: 20 }}
              />
            ) : (
              <View
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.primary + "20" }}
              >
                <Ionicons name="person" size={20} color={colors.primary} />
              </View>
            )}
            <View className="ml-3 flex-1">
              <Text
                style={{ color: colors.text }}
                className="font-semibold"
                numberOfLines={1}
              >
                {submitterName}
              </Text>
              {teamName && (
                <Text style={{ color: colors.textSecondary }} className="text-sm">
                  {teamName}
                </Text>
              )}
              {isSectionCompletion && sectionTitle && (
                <View className="flex-row items-center mt-1">
                  <Ionicons name="layers-outline" size={12} color={colors.primary} />
                  <Text style={{ color: colors.primary }} className="text-xs ml-1">
                    Sectie: {sectionTitle}
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* Status Badge */}
          <View
            className="px-2 py-1 rounded-full"
            style={{ backgroundColor: statusColor.bg }}
          >
            <Text className="text-xs font-medium" style={{ color: statusColor.text }}>
              {COMPLETION_STATUS_LABELS[submission.status]}
            </Text>
          </View>
        </View>

        {/* Timestamp */}
        <Text style={{ color: colors.textSecondary }} className="text-sm mb-3">
          Ingezonden{" "}
          {formatDistanceToNow(new Date(submission.completed_at), {
            addSuffix: true,
            locale: nl,
          })}
        </Text>

        {/* Points earned */}
        {submission.points_earned > 0 && (
          <View className="flex-row items-center mb-3">
            <Ionicons name="star" size={16} color="#F59E0B" />
            <Text style={{ color: "#F59E0B" }} className="ml-1 font-medium">
              {submission.points_earned} punten verdiend
            </Text>
          </View>
        )}

        {/* Section submission content */}
        {isSectionCompletion && (
          <View className="mb-3">
            {/* Text answer */}
            {textAnswer && (
              <View
                className="p-3 rounded-lg"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.textSecondary }} className="text-xs mb-1">
                  Antwoord:
                </Text>
                <Text style={{ color: colors.text }}>{textAnswer}</Text>
              </View>
            )}

            {/* Number value */}
            {numberValue !== null && numberValue !== undefined && (
              <View
                className="p-3 rounded-lg"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.textSecondary }} className="text-xs mb-1">
                  Waarde:
                </Text>
                <Text style={{ color: colors.text }} className="font-bold text-lg">
                  {numberValue}
                </Text>
              </View>
            )}

            {/* Checkbox completion */}
            {isCheckboxComplete && !textAnswer && numberValue === undefined && (
              <View className="flex-row items-center">
                <Ionicons name="checkmark-circle" size={20} color="#10B981" />
                <Text style={{ color: "#10B981" }} className="ml-2">
                  Taak voltooid
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Rejection reason */}
        {submission.status === "rejected" && submission.rejection_reason && (
          <View
            className="p-3 rounded-lg mb-3"
            style={{ backgroundColor: colors.error + "20" }}
          >
            <Text style={{ color: colors.error }} className="text-sm">
              Reden: {submission.rejection_reason}
            </Text>
          </View>
        )}

        {/* Action Buttons - Pending */}
        {isPending && (
          <View className="flex-row gap-3 mt-2">
            <TouchableOpacity
              onPress={onReject}
              className="flex-1 py-2 rounded-lg flex-row items-center justify-center"
              style={{ backgroundColor: colors.error + "20" }}
            >
              <Ionicons name="close" size={18} color={colors.error} />
              <Text style={{ color: colors.error }} className="ml-1 font-medium">
                Afkeuren
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={onApprove}
              className="flex-1 py-2 rounded-lg flex-row items-center justify-center"
              style={{ backgroundColor: colors.success + "20" }}
            >
              <Ionicons name="checkmark" size={18} color={colors.success} />
              <Text style={{ color: colors.success }} className="ml-1 font-medium">
                Goedkeuren
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Action Button - Approved (allow setting back to pending) */}
        {isApproved && (
          <TouchableOpacity
            onPress={onSetPending}
            className="py-2 rounded-lg flex-row items-center justify-center mt-2"
            style={{ backgroundColor: colors.warning + "20" }}
          >
            <Ionicons name="refresh" size={18} color={colors.warning} />
            <Text style={{ color: colors.warning }} className="ml-1 font-medium">
              Terug naar in afwachting
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

